import { useMutation } from "react-query";
import { auth } from "./firebase";
import { Method } from "./types";
import { QueryError, getQueryErrorCode } from "./errors";

export interface UseMutationParams<Res> {
  pathname?: string;
  method?: Method;
  server?: string;
  onSuccess?: (data: Res) => void;
  onError?: (error: Error) => void;
}

export interface MutationParams<Req, Res>
  extends Pick<UseMutationParams<Res>, "pathname"> {
  body?: Req;
}

const createRequestHeaders = async () => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (auth.currentUser) {
    const idToken = await auth.currentUser.getIdToken();
    headers["Authorization"] = `Bearer ${idToken}`;
  }
  return headers;
};

const useHttpMutation = <Req, Res>(params: UseMutationParams<Res>) => {
  const {
    pathname: hookPathname,
    method = "POST",
    server = "https://actions.dev.voxity.ai",
    onSuccess = () => {},
    onError = () => {},
  } = params;
  return useMutation<Res, QueryError, MutationParams<Req, Res>, unknown>(
    async (mParams: MutationParams<Req, Res>): Promise<Res> => {
      const { body, pathname: mutationPathname } = mParams;
      const pathname = mutationPathname || hookPathname;
      if (!pathname) {
        throw new Error("No pathname provided");
      }
      const url = new URL(pathname, server).toString();
      const response = await fetch(url, {
        method,
        headers: await createRequestHeaders(),
        body: body ? JSON.stringify(body) : null,
      });
      const contentType = response.headers.get("Content-Type");
      if (response.ok) {
        if (contentType?.includes("application/json"))
          return response.json() as Promise<Res>;
        return response.text() as Promise<Res>;
      }
      if (contentType?.includes("text/html")) {
        const result = await response.text();
        throw new QueryError(getQueryErrorCode(response.status), result);
      }
      const result = await response.json();
      throw result;
    },
    {
      onSuccess,
      onError,
    }
  );
};

export default useHttpMutation;
